/**
 * useFormStateChange
 *
 * Owns the "change form state" action for the active form: gates on required
 * fields, routes draft transitions straight through and non-draft transitions
 * via a permanent-change confirmation modal, then calls `setFormState`. Composed
 * by `FormWorkflowActionProvider` so the menus and the command palette share the
 * same validation, confirmation, and mutation. Returns the state handler and the
 * confirmation modal element the provider renders.
 */

import { useCallback, useState } from 'react';
import { Modal, Typography } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';
import { formStateDescription, type FormStateId } from '#types';
import { setFormState } from '@/services/artifactFunctions';
import { isDraftFormState } from './isDraftFormState';
import { checkRequiredFields } from './checkRequiredFields';

const { Text } = Typography;

type UseFormStateChangeParams = {
  formId: string;
  versionId: string | undefined;
  messageApi: MessageInstance;
  isCreating: boolean;
  setIsCreating: (creating: boolean) => void;
};

export const useFormStateChange = ({
  formId,
  versionId,
  messageApi,
  isCreating,
  setIsCreating,
}: UseFormStateChangeParams) => {
  const [isStateChangeModalOpen, setIsStateChangeModalOpen] = useState(false);
  const [pendingStateChange, setPendingStateChange] = useState<FormStateId | null>(null);

  const performStateChange = useCallback(
    async (state: FormStateId) => {
      if (!versionId) return;
      setIsCreating(true);
      try {
        await setFormState(formId, versionId, state);
      } catch (error) {
        messageApi.error(`Error changing form state: ${error instanceof Error ? error.message : String(error)}`);
      } finally {
        setIsCreating(false);
      }
    },
    [formId, versionId, messageApi, setIsCreating]
  );

  const handleSetState = useCallback(
    async (newState: FormStateId) => {
      if (!versionId) return;
      if (!checkRequiredFields()) return;

      if (!isDraftFormState(newState)) {
        setPendingStateChange(newState);
        setIsStateChangeModalOpen(true);
      } else {
        await performStateChange(newState);
      }
    },
    [versionId, performStateChange]
  );

  const handleStateChangeModalOk = useCallback(async () => {
    if (pendingStateChange) {
      await performStateChange(pendingStateChange);
      setPendingStateChange(null);
    }
    setIsStateChangeModalOpen(false);
  }, [pendingStateChange, performStateChange]);

  const handleStateChangeModalCancel = useCallback(() => {
    setPendingStateChange(null);
    setIsStateChangeModalOpen(false);
  }, []);

  const stateChangeModal = (
    <Modal
      title="Change Form State"
      open={isStateChangeModalOpen}
      onOk={handleStateChangeModalOk}
      onCancel={handleStateChangeModalCancel}
      confirmLoading={isCreating}
      okText="Yes"
      cancelText="No"
    >
      <Text>
        Are you sure you want to change the state to{' '}
        {pendingStateChange
          ? formStateDescription[pendingStateChange]?.shortLabel || pendingStateChange
          : ''}
        ?<br />
        <Text type="warning">This change is permanent.</Text>
      </Text>
    </Modal>
  );

  return { handleSetState, stateChangeModal };
};
